import { Button } from '@design-system/components/button'
import { Text } from '@design-system/components/Text'
import Link from '@/routing/Link'
import { cn } from '@/lib/utils'
import type { MemberUsage } from '@/lib/api'
import { getUserInitials } from '@/lib/utils/avatar'
import { UserAvatar } from '@/components/ui/UserAvatar'
import { OverageInfoTooltip } from './OverageInfoTooltip'
import { useTranslation } from 'react-i18next'
import { formatUsageCredits } from './dailyUsage'
import type { DailyUsageTeamMember, DailyUsageTotals } from '@/lib/api'

interface TeamUsageSectionProps {
  members: MemberUsage[]
  loading?: boolean
  /** Where the "Manage team" button should navigate. */
  manageTeamHref: string
  /** Per-credit overage rate in cents (current org plan/interval). */
  overageRateCents: number
  /**
   * Credit metering (enrolled): the team totals of the daily usage API, the same facts as the
   * usage log. When set, the table lists accrued usage and whole-credit debits per member.
   */
  daily?: { members: Array<DailyUsageTeamMember>; totals: DailyUsageTotals } | null
}

export function TeamUsageSection(props: TeamUsageSectionProps) {
  const { members, loading, manageTeamHref, overageRateCents, daily } = props
  const { t } = useTranslation('app')

  if (daily) {
    return (
      <div className="flex flex-col gap-4" data-testid="billing-team-usage">
        <div className="flex items-center justify-between">
          <Text size="sm" weight="medium">
            Team usage
          </Text>
          <Link href={manageTeamHref}>
            <Button
              variant="outline"
              size="sm"
              data-testid="billing-manage-team"
              className="h-9 gap-1.5 rounded-md border-cta px-3 text-sm font-medium text-cta hover:bg-cta/5 dark:border-cta dark:text-cta dark:hover:bg-cta/10"
            >
              Manage team
            </Button>
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[360px] border-separate border-spacing-0 text-left text-sm" data-testid="billing-team-usage-daily">
            <thead>
              <tr>
                <th scope="col" className="rounded-l-xl bg-muted/50 px-5 py-3 text-xs font-normal text-muted-foreground">Member</th>
                <th scope="col" className="bg-muted/50 px-5 py-3 text-right text-xs font-normal text-muted-foreground">{t('billing.colCredits')}</th>
                <th scope="col" className="rounded-r-xl bg-muted/50 px-5 py-3 text-right text-xs font-normal text-muted-foreground">{t('billing.usage.colDebited')}</th>
              </tr>
            </thead>
            <tbody>
              {daily.members.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-5 py-8 text-center text-sm text-muted-foreground">No team members yet.</td>
                </tr>
              ) : null}
              {daily.members.map((member) => (
                <tr key={member.userToken} data-testid={`billing-team-usage-daily-${member.userToken}`}>
                  <td className="border-b border-border/40 px-5 py-3.5 text-foreground dark:border-border">{member.displayName ?? member.userToken}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-right tabular-nums text-foreground dark:border-border">{formatUsageCredits(member.accruedMillicredits)}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-right tabular-nums text-muted-foreground dark:border-border">{member.postedCredits}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr data-testid="billing-team-usage-daily-total">
                <td className="px-5 py-3.5 text-xs text-muted-foreground">{t('billing.usage.teamTotal')}</td>
                <td className="px-5 py-3.5 text-right tabular-nums font-medium text-foreground">{formatUsageCredits(daily.totals.accruedMillicredits)}</td>
                <td className="px-5 py-3.5 text-right tabular-nums text-muted-foreground">{daily.totals.postedCredits}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4" data-testid="billing-team-usage">
      <div className="flex items-center justify-between">
        <Text size="sm" weight="medium">
          Team usage
        </Text>
        <Link href={manageTeamHref}>
          <Button
            variant="outline"
            size="sm"
            data-testid="billing-manage-team"
            className="h-9 gap-1.5 rounded-md border-cta px-3 text-sm font-medium text-cta hover:bg-cta/5 dark:border-cta dark:text-cta dark:hover:bg-cta/10"
          >
            Manage team
          </Button>
        </Link>
      </div>

      <div className="overflow-hidden">
        <table className="w-full border-separate border-spacing-0 text-left text-sm">
          <thead>
            <tr>
              <th scope="col" className="rounded-l-xl bg-muted/50 px-5 py-3 text-xs font-normal text-muted-foreground">
                Member
              </th>
              <th scope="col" className="bg-muted/50 px-5 py-3 text-xs font-normal text-muted-foreground">
                Credits used
              </th>
              <th scope="col" className="w-[140px] rounded-r-xl bg-muted/50 px-5 py-3 text-right text-xs font-normal text-muted-foreground">
                Part
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={3} className="px-5 py-8 text-center text-sm text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && members.length === 0 && (
              <tr>
                <td colSpan={3} className="px-5 py-8 text-center text-sm text-muted-foreground">
                  No team members yet.
                </td>
              </tr>
            )}
            {members.map((m, idx) => {
              const pct = m.creditLimit > 0 ? Math.round((m.used / m.creditLimit) * 100) : 0
              const isOver = m.isPoolBilling ? false : pct > 100
              const fillPct = Math.min(pct, 100)
              const role = m.orgRole
                ? m.orgRole[0]!.toUpperCase() + m.orgRole.slice(1)
                : null
              const initials = getUserInitials(m.name, m.email)
              const isLastRow = idx === members.length - 1
              const cellBorder = isLastRow ? '' : 'border-b border-border/40 dark:border-border'

              return (
                <tr key={m.userId} className="align-middle">
                  <td className={`px-5 py-3.5 ${cellBorder}`}>
                    <div className="flex items-center gap-3">
                      <UserAvatar
                        initials={initials}
                        name={m.name || m.email}
                        size="xs"
                      />
                      <div className="min-w-0">
                        <div className="truncate text-sm">
                          <span className="font-medium text-foreground">
                            {m.name || m.email}
                          </span>
                          {role ? (
                            <span className="ml-1 text-muted-foreground">({role})</span>
                          ) : null}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {m.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className={`px-5 py-3.5 ${cellBorder}`}>
                    <div className="flex items-center gap-3">
                      <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                        <div
                          className={cn(
                            'h-full rounded-full transition-all',
                            isOver ? 'bg-destructive' : 'bg-chart-positive',
                          )}
                          style={{ width: `${fillPct}%` }}
                        />
                        {isOver ? (
                          <span
                            aria-hidden
                            className="absolute right-0 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-destructive ring-2 ring-card-inset"
                          />
                        ) : null}
                      </div>
                      <span
                        className={cn(
                          'shrink-0 text-xs tabular-nums',
                          isOver ? 'font-medium text-destructive' : 'text-muted-foreground',
                        )}
                      >
                        {m.isPoolBilling
                          ? `${m.used.toLocaleString()} credits`
                          : `${m.used.toLocaleString()}/${m.creditLimit.toLocaleString()}`}
                      </span>
                    </div>
                  </td>
                  <td className={`px-5 py-3.5 text-right ${cellBorder}`}>
                    <div className="inline-flex items-center justify-end gap-1">
                      <span
                        className={cn(
                          'tabular-nums',
                          isOver ? 'font-medium text-destructive' : 'text-foreground',
                        )}
                        data-testid={`billing-team-usage-pct-${m.userId}`}
                      >
                        {pct}%
                      </span>
                      {isOver ? (
                        <OverageInfoTooltip
                          overageCredits={m.overageUsed}
                          rateCents={overageRateCents}
                          side="left"
                          label={`Overage details for ${m.name || m.email}`}
                          triggerTestId={`billing-team-usage-overage-info-${m.userId}`}
                        />
                      ) : null}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
